(ns nosco.ui.welcome.welcome-components
  (:require
   ["react" :as React]
   [goog.object :as gobj]
   [hx.hooks :refer [useLayoutEffect useState]]
   [hx.react :as hx :refer [defnc]]
   [nosco.i18n.translate :as t]
   [nosco.icons2 :as icons2]
   [nosco.ui.buttons2 :as buttons]
   [nosco.ui.dropdowns2 :as dropdowns2]
   [nosco.ui.html :as html]
   [nosco.ui.menus :as menus]
   [nosco.ui.messages :as messages]
   [nosco.ui.modals3 :as modals3]
   [nosco.ui.text :as text]
   [nosco.ui.tooltips :as tooltips]))

(defnc WelcomeContainer [{:keys [children]}]
  [:div {:class "welcome-main"}
   children])

(defnc LoginLogo [{:keys [img href]}]
  [:a {:href href}
   [:img {:src img
          :class "welcome-header__logo"}]])

(defnc Header [{:keys [login-logo login-link title intro]}]
  [:div {:class "welcome-header"}
   (when login-logo
     [LoginLogo {:img login-logo
                 :href login-link}])
   (when title
     [:h1 {:class "welcome-header__title"}
      title])
   (when intro
     [:div {:class "welcome-header__info"}
      intro])])

(defnc Footer [{:keys [powered-by-text]}]
  [:div {:class "welcome-footer"}
   [:a {:href "https://www.nos.co"
        :target "_blank"
        :class "welcome-powered-by"}
    [:span powered-by-text]
    [icons2/NoscoLogo {:class "welcome-powered-by-logo"}]]])

(defnc MainImage [{:keys [image-url background-size site-color]}]
  [:div {:class "welcome-image"
         :style {:background-color (str "#" site-color)
                 :background-size background-size}}
   [:img {:src image-url
          :loading "lazy"
          :style {:object-fit background-size}}]])

(defnc TextModal [{:keys [title html showing close]}]
  [modals3/ModalDialog {:showing showing
                        :title title
                        :on-close close}
   [html/SafeHTML {:class "nosco-redactor-text nosco-redactor-text--small"
                   :html html}]])

(defnc Recaptcha
  "A component that renders an invisible recaptcha. Needs a recaptcha site key,
  a callback function (plain js function) and a callback-name which must be a globally unique
  and valid js function identifier. The function will be assigned to this identifier in the
  global window context, so that recaptcha can find and call it."
  [{:keys [site-key callback-fn callback-name]
    :or {callback-name "onRecaptcha"}}]
  (let [ref (React/useRef)
        rendered? (React/useRef)]
    (useLayoutEffect
     (fn []
       (gobj/set js/window callback-name callback-fn)
       (when-not (.-current rendered?)
         (set! (.-current rendered?) true)
         (.render js/grecaptcha (.-current ref)))
       (fn []
         (gobj/remove js/window callback-name)))
     [callback-name callback-fn])
    [:div {:data-sitekey site-key
           :ref ref
           :data-callback callback-name
           :data-size "invisible"
           :class "g-recaptcha"}]))

(defnc ErrorMessage [{:keys [title text]}]
  [messages/SectionMessage {:title title
                            :variant :error}
   text])

(defnc SuccessMessage [{:keys [title text]}]
  [messages/SectionMessage {:title title
                            :variant :success}
   text])

(defnc WarningMessage [{:keys [title text]}]
  [messages/SectionMessage {:title title
                            :variant :warning}
   text])

(defnc Form [{:keys [title help children]}]
  [:div {:class "welcome-form"}
   [:h2 {:class "welcome-form__title"} title]
   (when help
     [:p {:class "welcome-form__help"}
      help])
   [:div {:class "mb-spacing-40"}]
   children])

(defnc Or [{:keys [text]}]
  [:div {:class "welcome-form__or"}
   [:div {:class "welcome-form__or-ruler"}]
   [:div
    [text/Text {:variant text/Button
                :el :span
                :class "text-neutral-700"} text]]
   [:div {:class "welcome-form__or-ruler"}]])

(defnc InlineButtonWrapper [{:keys [children]}]
  [:div {:class "welcome-form__inline-button-wrapper"}
   children])

(defnc ButtonsList [{:keys [children]}]
  [:div {:class "welcome-form__buttons-list"}
   children])

(defnc HelpMenu [{:keys [sections]}]
  [:div {:class "welcome-form__helpmenu"}
   [dropdowns2/Dropdown
    {:button (hx/f [buttons/Button {:variant :secondary
                                    :size :large
                                    :icon icons2/Question-small}])
     :popper-options {:offset [0 8]
                      :placement "top-end"}}
    (fn [{:keys [close]}]
      [menus/Menu {:keyboard-nav true}
       (for [{:keys [title items]} sections]
         [menus/MenuSection
          (when title
            [:div {:class ["py-spacing-8" "px-spacing-16" "mb-spacing-8"]}
             [text/Text {:variant text/Heading-Small}
              title]])
          (when items
            [menus/MenuItems
             (for [{:keys [label href icon target] :as item} items
                   :when item]
               [menus/MenuItem
                {:label label
                 :href href
                 :icon icon
                 :target target
                 :on-click #(close)}])])])])]])

(defnc ContinueWithButton [{{:keys [label tooltip href icon variant]} :provider icon-only? :icon-only?}]
  (let [[loading set-loading] (useState false)]
    [tooltips/WithTooltip
     {:label (cond
               tooltip tooltip
               icon-only? label)}
     [buttons/Link
      {:variant variant
       :icon icon
       :href href
       :disabled loading
       :loading loading
       :on-click (fn [e]
                   (set-loading true))
       :title label
       :label (when-not icon-only? label)}]]))

(defnc AuthProviderButtons [{:keys [inline? providers]}]
  [(if inline? InlineButtonWrapper :<>)
   (for [provider providers
         :when provider]
     [ContinueWithButton {:provider provider
                          :icon-only? inline?}])])

(defnc SocialButtons [{:keys [inline social-providers sso-provider variant]}]
  (let [{:keys [google facebook linkedin]} social-providers
        social-signup? (some identity (vals social-providers))
        all-providers
        (remove nil?
                [(when facebook
                   {:label (t/t "Continue with Facebook")
                    :variant :social-facebook
                    :href "/authorize/social/facebook"})
                 (when google
                   {:label (t/t "Continue with Google")
                    :variant :social-google
                    :href "/authorize/social/google"})
                 (when linkedin
                   {:label (t/t "Continue with LinkedIn")
                    :variant :social-linkedin
                    :href "/authorize/social/linkedin"})
                 (when sso-provider
                   (assoc sso-provider
                          :icon icons2/User-Badge-small
                          :variant (if ;; if the buttons are "inline", that is
                                       ;; they should take only one row, that is because
                                       ;; the form is also showing...
                                       ;; but SSO sits alone (because there's no social signup buttons)
                                       ;; turn it white so that it doesn't clash with the login button being primary.
                                       (and inline (not social-signup?))
                                     :secondary
                                     variant)))])]
    [AuthProviderButtons
     {:inline? (and inline (> (count all-providers) 1))
      :providers all-providers}]))

(defnc ContinueWithEmailButton [{:keys [on-click label tooltip variant]}]
  [tooltips/WithTooltip {:label tooltip}
   [buttons/Button
    {:variant variant
     :on-click on-click
     :label label}]])

(defnc ContinueWithEmailButtonOrFormInline [{:keys [show-form-inline set-show-form-inline email-button-text variant form]}]
  (if show-form-inline
    form
    [ContinueWithEmailButton
     {:on-click (fn []
                  (set-show-form-inline true))
      :label (:title email-button-text)
      :tooltip (:tooltip email-button-text)
      :variant variant}]))

(defnc FlowChoiceForm [{:keys [social-providers sso-enabled? sso-provider email-button-text login-method-buttons-order form]}]
  (let [social-signup? (some identity (vals social-providers))
        email-only? (and (not sso-enabled?) (not social-signup?))
        [show-form-inline set-show-form-inline] (useState false)]
    (if
     email-only?
      form
      [ButtonsList
       (if (or (nil? login-method-buttons-order)
               (= login-method-buttons-order "sso"))
         [:<>
          [SocialButtons {:inline show-form-inline
                          :social-providers social-providers
                          :sso-provider sso-provider
                          :variant :primary-black}]
          [Or {:text (t/t "or")}]
          [ContinueWithEmailButtonOrFormInline {:email-button-text email-button-text
                                                :form form
                                                :show-form-inline show-form-inline
                                                :set-show-form-inline set-show-form-inline
                                                :variant :secondary}]]
         [:<>
          [ContinueWithEmailButtonOrFormInline {:email-button-text email-button-text
                                                :form form
                                                :show-form-inline show-form-inline
                                                :set-show-form-inline set-show-form-inline
                                                :variant :primary-black}]
          [Or {:text (t/t "or")}]
          [SocialButtons {:inline show-form-inline
                          :social-providers social-providers
                          :sso-provider sso-provider
                          :variant :secondary}]])])))

(defnc LoginArea [{:keys [sso-enabled? signup-enabled? social-providers sso-provider email-button-text login-method-buttons-order login-form]}]
  [Form {:title (t/t "Login")
         :help (when signup-enabled?
                 [:<>
                  (t/t "New here?")
                  " "
                  [:a {:href "/welcome"
                       :style {:text-decoration "underline"}} (t/t "Create account")]])}
   [FlowChoiceForm {:social-providers social-providers
                    :sso-enabled? sso-enabled?
                    :sso-provider sso-provider
                    :email-button-text email-button-text
                    :login-method-buttons-order login-method-buttons-order
                    :form login-form}]])

(defnc Signup [{:keys [social-providers sso-enabled? sso-provider email-button-text login-method-buttons-order signup-form]}]
  [Form {:title (t/t "Create account")
         :help [:<>
                (t/t "Already have an account?")
                " "
                [:a {:href "/login"
                     :style {:text-decoration "underline"}}
                 (t/t "Login instead")]]}
   [FlowChoiceForm {:social-providers social-providers
                    :sso-enabled? sso-enabled?
                    :sso-provider sso-provider
                    :email-button-text email-button-text
                    :login-method-buttons-order login-method-buttons-order
                    :form signup-form}]])

(defnc ResetPassword [{:keys [parameters password-rules children]}]
  [Form {:title (if (-> parameters :query :activate)
                  (t/t "Activate account")
                  (t/t "Create new password"))
         :help (cond (-> parameters :query :activate)
                     (t/t "Create a password to activate your account and access the platform")
                     (:uniqueHistory password-rules)
                     (t/t "Your password must be different from previously used passwords")
                     :else
                     (t/t "We suggest to avoid reusing previously used passwords"))}
   children])

(defnc TermsContainer [{:keys [children]}]
  [:div {:class "welcome-form__terms"}
   children])

(defnc RecaptchaText []
  [:div {:class "welcome-form__terms-text"}
   (t/t-html "This site is protected by ReCAPTCHA and the <a href='%s'>Google Privacy Policy</a> and <a href='%s'>Terms of Services</a> apply."
             "https://policies.google.com/privacy"
             "https://policies.google.com/terms")])

(defnc TermsConditionsText [{:keys [terms-conditions-link-title terms-conditions-display
                                    privacy-policy-link-title privacy-policy-display]}]
  (let [terms-conditions-display (= "show" terms-conditions-display)
        privacy-policy-display (= "show" privacy-policy-display)]
    (when (or terms-conditions-display privacy-policy-display)
      [:div {:class "welcome-form__terms-text"}
       (cond (and terms-conditions-display privacy-policy-display)
             (t/t-html "By clicking Create account, I agree that I have read and accepted the <a href=\"%s\">%s</a> and <a href=\"%s\">%s</a>."
                       "?modal=terms-conditions"
                       (or terms-conditions-link-title (t/t "Terms & Conditions"))
                       "?modal=privacy-policy"
                       (or privacy-policy-link-title (t/t "Privacy Policy")))
             terms-conditions-display
             (t/t-html "By clicking Create account, I agree that I have read and accepted the <a href=\"%s\">%s</a>."
                       "?modal=terms-conditions"
                       (or terms-conditions-link-title (t/t "Terms & Conditions")))
             privacy-policy-display
             (t/t-html "By clicking Create account, I agree that I have read and accepted the <a href=\"%s\">%s</a>."
                       "?modal=privacy-policy"
                       (or privacy-policy-link-title (t/t "Privacy Policy"))))])))
